import React, { Component } from 'react';
import './SilverExecSelector.scss';
import { getContentUrl, ContentURL } from '../../modules/Utils/ContentURL';
import { IsMobileDevice } from '../../utils/DetectDevice';
import { Dispatch } from '../../modules/Dispatch';
import { BookingTimeV2 } from '../../modules/GbBooking/BookingV2Entities';
import { DateTime } from 'luxon';
import { FeatureFlags } from '../../Config/FeatureFlags';
import { ZONES_EXCLUDING_STANDARD_SILVER_SERVICE } from '../../modules/CuratedLocations/CuratedLocationZones';

interface SilverExecSelectorProps {
    MaxCapacity: number;
    PassengerCount: number;
    LuggageCount: number;
    FlightNumber: string;
    IsPriceGuaranteeSelected: boolean;
    BookingTime: BookingTimeV2;
    PickupTimeZoneId: string | null;
    PickupZoneId: number | null;
    OnPassengerChange: (count: number) => void;
    OnLuggageChange: (count: number) => void;
    OnFlightNumberChange: (flight: string) => void;
}

class SilverExecSelector extends Component<SilverExecSelectorProps> {
    private luggageDecreaseRef: React.RefObject<HTMLImageElement>;

    constructor(props: SilverExecSelectorProps) {
        super(props);
        this.handlePassengerChange = this.handlePassengerChange.bind(this);
        this.handleLuggageChange = this.handleLuggageChange.bind(this);
        this.handleFlightNumberChange = this.handleFlightNumberChange.bind(this);
        this.luggageDecreaseRef = React.createRef();
    }

    componentDidMount() {
        this.validateAndDispatch();
        // Focus on luggage selector when component mounts
        this.focusLuggageSelector();
    }

    componentDidUpdate() {
        this.validateAndDispatch();
    }

    /**
     * Focus the luggage selector (first interactive element in luggage section)
     */
    focusLuggageSelector() {
        if (this.luggageDecreaseRef.current) {
            this.luggageDecreaseRef.current.focus();
        }
    }

    /**
     * Check if pickup location is in a restricted zone where Maxi Taxi is not available.
     * In restricted zones, text should not mention "MAXI TAXI" as it's not available.
     */
    private isInRestrictedZone(): boolean {
        return this.props.PickupZoneId !== null && 
               ZONES_EXCLUDING_STANDARD_SILVER_SERVICE.includes(this.props.PickupZoneId);
    }

    /**
     * Check if the requested booking time violates the 2-hour minimum advance booking requirement.
     * Returns true if booking is immediate OR within 2 hours from now.
     * 
     * @returns true if booking violates 2-hour minimum, false otherwise
     */
    private isWithinTwoHourMinimum(): boolean {
        // Immediate bookings always violate the 2-hour requirement
        if (this.props.BookingTime.IsImmediate) {
            return true;
        }

        // Book for later - check if requested time is within 2 hours
        const requestedTime = this.props.BookingTime.RequestedDate;
        if (!requestedTime) {
            return false;
        }

        // Use pickup location's timezone if available, otherwise fall back to browser timezone
        const pickupZone = this.props.PickupTimeZoneId || DateTime.now().zoneName;
        
        // Normalize times to minute precision (strip seconds/milliseconds) to avoid edge cases
        const nowInPickupZone = DateTime.now().setZone(pickupZone).startOf('minute');
        const requestedTimeNormalized = requestedTime.startOf('minute');
        const twoHoursFromNow = nowInPickupZone.plus({ hours: 2 });
        
        return requestedTimeNormalized < twoHoursFromNow;
    }

    /**
     * Validate current state and dispatch error/clear to Redux
     */
    validateAndDispatch() {
        // Check if Fixed Price is OFF
        if (!this.props.IsPriceGuaranteeSelected) {
            Dispatch.Booking.SetSilverExecError("Fixed Price required");
            return;
        }

        // Check 2-hour minimum advance booking for both Exec and XL (only if feature flag is enabled)
        if (FeatureFlags.SilverExecTwoHourMinimum && this.isWithinTwoHourMinimum()) {
            Dispatch.Booking.SetSilverExecError("Silver Service Executive can only be requested a minimum of 2 hours in advance. For earlier than 2 hours please call 13 31 00.");
            return;
        }

        // Check if XL is over capacity
        if (this.props.MaxCapacity === 7 && this.isOverCapacity()) {
            Dispatch.Booking.SetSilverExecError("Over capacity");
            return;
        }

        // All valid - clear error
        Dispatch.Booking.ClearSilverExecError();
    }

    /**
     * Calculate the recommended maximum luggage based on passenger count
     * XL (7 seats): 7 PAX→3, 6 PAX→4, 5 PAX→5, 4-1 PAX→6
     * Exec (4 seats): 2 luggage max
     */
    getRecommendedMaxLuggage(passengers: number): number {
        // Silver Service Exec (MaxCapacity 4) - max 2 luggage
        if (this.props.MaxCapacity === 4) {
            return 2;
        }
        
        // Silver Service Exec XL (MaxCapacity 7)
        if (passengers === 7) return 3;
        if (passengers === 6) return 4;
        if (passengers === 5) return 5;
        return 6; // 4 or fewer passengers
    }

    /**
     * Check if current luggage count exceeds recommended capacity
     */
    isOverCapacity(): boolean {
        const recommended = this.getRecommendedMaxLuggage(this.props.PassengerCount);
        return this.props.LuggageCount > recommended;
    }

    handlePassengerChange(delta: number) {
        const newCount = this.props.PassengerCount + delta;
        
        // Enforce: passengers >= 1 and passengers <= MaxCapacity (hard limit)
        if (newCount >= 1 && newCount <= this.props.MaxCapacity) {
            this.props.OnPassengerChange(newCount);
        }
    }

    handleLuggageChange(delta: number) {
        const newCount = this.props.LuggageCount + delta;
        const recommended = this.getRecommendedMaxLuggage(this.props.PassengerCount);
        
        // Silver Service Exec: Hard limit at 2 luggage (no warning, no exceeding)
        if (this.props.MaxCapacity === 4) {
            if (newCount >= 0 && newCount <= 2) {
                this.props.OnLuggageChange(newCount);
            }
        }
        // Silver Service Exec XL: Allow up to recommended + 1 (warning shown when > recommended)
        else {
            if (newCount >= 0 && newCount <= recommended + 1) {
                this.props.OnLuggageChange(newCount);
            }
        }
    }

    handleFlightNumberChange(event: React.ChangeEvent<HTMLInputElement>) {
        this.props.OnFlightNumberChange(event.target.value);
    }

    handleKeyDown(delta: number, handler: (delta: number) => void, event: React.KeyboardEvent) {
        if (event.key === "Enter") {
            handler(delta);
        }
    }

    render() {
        const isMobile = IsMobileDevice();

        return (
            <div className="silver-exec-selector-panel">
                {/* Row 1: Passengers */}
                <div className="silver-exec-row">
                    <div className="silver-exec-selector-col">
                        <span className="silver-exec-label">Number of passengers</span>
                        <div className="silver-exec-selector-box">
                            <img
                                onClick={() => this.handlePassengerChange(-1)}
                                className="position-absolute-left"
                                src={getContentUrl(ContentURL.images.buttons.PickerMinus)}
                                alt="Decrease passengers"
                                tabIndex={0}
                                onKeyDown={(e) => this.handleKeyDown(-1, this.handlePassengerChange, e)}
                            />
                            <span>{this.props.PassengerCount}</span>
                            <img
                                onClick={() => this.handlePassengerChange(1)}
                                className="position-absolute-right"
                                src={getContentUrl(ContentURL.images.buttons.PickerPlus)}
                                alt="Increase passengers"
                                tabIndex={0}
                                onKeyDown={(e) => this.handleKeyDown(1, this.handlePassengerChange, e)}
                            />
                        </div>
                    </div>
                    <div className="silver-exec-helper-col">
                        {this.props.MaxCapacity === 4 ? (
                            <span className="silver-exec-helper-text">
                                {this.isInRestrictedZone() 
                                    ? "For more than 4 passengers select a larger vehicle."
                                    : "For more than 4 passengers select Silver Service XL or MAXI TAXI."}
                            </span>
                        ) : (
                            <span className="silver-exec-helper-text">
                                {this.isInRestrictedZone() 
                                    ? "Maximum 7 passengers"
                                    : "For more than 7 passengers select MAXI TAXI."}
                            </span>
                        )}
                    </div>
                </div>

                {/* Row 2: Luggage */}
                <div className="silver-exec-row">
                    <div className="silver-exec-selector-col">
                        <span className="silver-exec-label">Number of check-in suitcases</span>
                        <div className="silver-exec-selector-box">
                            <img
                                ref={this.luggageDecreaseRef}
                                onClick={() => this.handleLuggageChange(-1)}
                                className="position-absolute-left"
                                src={getContentUrl(ContentURL.images.buttons.PickerMinus)}
                                alt="Decrease luggage"
                                tabIndex={0}
                                onKeyDown={(e) => this.handleKeyDown(-1, this.handleLuggageChange, e)}
                            />
                            <span>{this.props.LuggageCount}</span>
                            <img
                                onClick={() => this.handleLuggageChange(1)}
                                className="position-absolute-right"
                                src={getContentUrl(ContentURL.images.buttons.PickerPlus)}
                                alt="Increase luggage"
                                tabIndex={0}
                                onKeyDown={(e) => this.handleKeyDown(1, this.handleLuggageChange, e)}
                            />
                        </div>
                    </div>
                    <div className="silver-exec-helper-col">
                        {this.props.MaxCapacity === 4 ? (
                            <span className="silver-exec-helper-text">
                                {this.isInRestrictedZone() 
                                    ? "For more than 2 check-in suitcases select a larger vehicle."
                                    : "For more than 2 check-in suitcases select Silver Service XL or MAXI TAXI."}
                            </span>
                        ) : (
                            <span className="silver-exec-helper-text">
                                This will let us know if additional space is required.
                            </span>
                        )}
                    </div>
                </div>

                {/* Row 3: Flight Number */}
                <div className="silver-exec-row">
                    <div className="silver-exec-selector-col">
                        <span className="silver-exec-label">Flight Number (Optional)</span>
                        <input
                            id="flight-number-input"
                            type="text"
                            className="silver-exec-flight-input"
                            value={this.props.FlightNumber}
                            onChange={this.handleFlightNumberChange}
                            placeholder="e.g. QF123"
                            maxLength={20}
                        />
                    </div>
                    <div className="silver-exec-helper-col">
                        <span className="silver-exec-helper-text">
                            This helps us track your ETA if there are flight delays. Leave blank if not applicable.
                        </span>
                    </div>
                </div>

                {/* Warning Message - Show when booking is immediate OR within 2 hours (only if feature flag is enabled) */}
                {FeatureFlags.SilverExecTwoHourMinimum && this.isWithinTwoHourMinimum() && (
                    <div className="silver-exec-capacity-warning">
                        <span className="warning-icon">⚠</span>
                        <span className="warning-text">
                            Silver Service Executive can only be requested a minimum of 2 hours in advance. For earlier than 2 hours please call 13 31 00.
                        </span>
                    </div>
                )}

                {/* Warning Message - Show only for XL when luggage exceeds recommended capacity */}
                {this.props.MaxCapacity === 7 && this.isOverCapacity() && (
                    <div className="silver-exec-capacity-warning">
                        <span className="warning-icon">⚠</span>
                        <span className="warning-text">
                            {this.isInRestrictedZone() 
                                ? "This Silver Service Executive vehicle exceeds maximum capacity."
                                : "Insufficient capacity. Please select MAXI TAXI to accommodate the extra space."}
                        </span>
                    </div>
                )}

                {/* Warning Message - Show when Fixed Price is not selected for Silver Service Exec/XL */}
                {!this.props.IsPriceGuaranteeSelected && (
                    <div className="silver-exec-capacity-warning">
                        <span className="warning-icon">⚠</span>
                        <span className="warning-text">
                            Fixed Price must be switched on above when selecting Silver Service Executive
                        </span>
                    </div>
                )}
            </div>
        );
    }
}

export default SilverExecSelector;
